import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { IoSend, IoCheckmarkDoneSharp } from "react-icons/io5";
import { BsRobot } from "react-icons/bs";
import { FiUser } from "react-icons/fi";
import Markdown from "marked-react";
import { MdOutlineContentCopy } from "react-icons/md";
import { PiChatTeardropTextFill } from "react-icons/pi";
import { LuClipboardCheck } from "react-icons/lu";
import { FaAngleLeft, FaMinus } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { FaPlus } from "react-icons/fa";
import { allowScroll, preventScroll } from "./Utils";
import { RemoteRunnable } from "@langchain/core/runnables/remote";

const icon =
  "https://spinnakerwebsiteassets.s3.amazonaws.com/spinnakeranalytics.com/images/logo/spinnaker-analytics-icon.webp";

const logo =
  "https://spinnakerwebsiteassets.s3.amazonaws.com/spinnakeranalytics.com/images/logo/spinnaker-analytics-logo.webp";

axios.defaults.withCredentials = true;

const ChatBot = () => {
  // const baseURL = "http://localhost:8000";
  const baseURL = "https://sage-agentic-rag-backend.azurewebsites.net";
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState(() => {
    const savedHistory = sessionStorage.getItem("chatbot_history");
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  // Save chat history to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("chatbot_history", JSON.stringify(chatHistory));
  }, [chatHistory]);

  const [load, setLoad] = useState(false);
  const [newConversation, setNewConversation] = useState(false);
  const [clipboard, setClipboard] = useState(null);
  const [botOpen, setBotOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [truncatedMessages, setTruncatedMessages] = useState(new Set());
  const [showUpMsg, setShowUpMsg] = useState(true);
  const [showFAQs, setShowFAQs] = useState(false);
  const [streamingMessageIndex, setStreamingMessageIndex] = useState(null);

  const FAQ_Question = [
    "What is Spinnaker?",
    "What does Spinnaker do?",
    "What industries do you support?",
    "What functions do you support?",
  ];

  const handleOnChange = (e) => {
    setMessage(e.target.value);
  };

  const startNewConversation = () => {
    // Clear both thread_id and chat history
    sessionStorage.removeItem("chatbot_thread_id");
    sessionStorage.removeItem("chatbot_history");
    const newThreadId = crypto.randomUUID();
    sessionStorage.setItem("chatbot_thread_id", newThreadId);
    console.log("session removed ");

    setMessage("");
    setLoad(false);
    setChatHistory([]);
    setNewConversation(false);
    setPage("SAGE");
    setThreadId(newThreadId);
    setStreamingMessageIndex(null);
  };

  const scrollToBottom = useRef(null);

  const [threadId, setThreadId] = useState(() => {
    const existingThreadId = sessionStorage.getItem("chatbot_thread_id");
    if (existingThreadId) {
      return existingThreadId;
    } else {
      const newThreadId = crypto.randomUUID();
      sessionStorage.setItem("chatbot_thread_id", newThreadId);
      return newThreadId;
    }
  });

  const onMessageSend = async (e, option = false) => {
    if (option) {
      setMessage("");
      setPage("SAGE");
      setNewConversation(false);
    } else {
      e.preventDefault();
    }

    if (message || option) {
      const userMsg = option ? option : message;

      // Add user message
      setChatHistory((prevChat) => [
        ...prevChat,
        {
          chat: userMsg,
          sender: "human",
          time: new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);

      setLoad(true);
      setMessage("");
      setShowFAQs(false);

      // Add empty bot message that we'll stream into
      const botMessageIndex = chatHistory.length + 1; // +1 because we just added user message
      setStreamingMessageIndex(botMessageIndex);

      setChatHistory((prevChat) => [
        ...prevChat,
        {
          chat: "",
          sender: "bot",
          time: new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);

      if (userMsg.trim() === "") return;

      try {
        // Create RemoteRunnable instance
        const chain = new RemoteRunnable({
          url: `${baseURL}/adaptive_rag/`,
        });

        const input = {
          question: userMsg,
        };

        const config = {
          configurable: {
            thread_id: threadId,
          },
          version: "v2",
        };

        // Stream events directly
        const eventStream = await chain.streamEvents(input, config, {});

        let accumulatedResponse = "";
        let finalResponse = null;

        for await (const event of eventStream) {
          // Log all events to see what's available
          //  console.log("Event received at:", new Date().toISOString());
          //  console.log("Event type:", event.event);
          //  console.log("Event data:", event.data);

          // Capture LLM streaming tokens
          // console.log(event);

          if (
            event.event === "on_llm_stream" ||
            event.event === "on_chat_model_stream"
          ) {
            if (event.metadata.langgraph_node !== "rewrite_followup") {
              const chunk = event.data?.chunk;
              const token = chunk?.text ?? chunk?.content ?? "";
              //  console.log(chunk);

              if (token) {
                // console.log("********************Token received:", token);
                accumulatedResponse += token;
                await new Promise((resolve) => setTimeout(resolve, 50));
                // Update the bot message in real-time
                setChatHistory((prevChat) =>
                  prevChat.map((msg, index) =>
                    index === botMessageIndex
                      ? { ...msg, chat: accumulatedResponse }
                      : msg
                  )
                );
              }
            }
          }

          if (event.event === "on_chain_end") {
            let chain_end_msg = event?.data?.output?.lc_kwargs?.messages;
            if (Array.isArray(chain_end_msg) && chain_end_msg?.length > 0) {
              let airesponse = chain_end_msg[chain_end_msg.length - 1];
              if (typeof airesponse.content === "string") {
                // console.log(airesponse);
                try {
                  airesponse = airesponse?.content?.replace(/'/g, '"');
                  finalResponse = JSON.parse(airesponse);
                } catch (error) {
                  finalResponse = null;
                  // console.log("err");
                  
                }
              }
            }
          }
        }

        // console.log("**Final", finalResponse);
        // console.log(typeof(finalResponse));
        // console.log("**Final: ", finalResponse?.generation);
        if (finalResponse !== null) {
          setChatHistory((prevChat) =>
            prevChat.map((msg, index) =>
              index === botMessageIndex
                ? { ...msg, chat: finalResponse?.generation }
                : msg
            )
          );
        }
        console.log("Stream complete.");
      } catch (error) {
        console.error("Error streaming:", error);

        // Update with error message
        setChatHistory((prevChat) =>
          prevChat.map((msg, index) =>
            index === botMessageIndex
              ? {
                  ...msg,
                  chat: "Sorry, I encountered an error. Please try again.",
                }
              : msg
          )
        );
      } finally {
        setLoad(false);
        // setStreamingMessageIndex(null);
        // console.log(chatHistory);
      }
    }
  };

  useEffect(() => {
    scrollToBottom?.current?.scrollIntoView({
      behavior: "smooth",
      bottom: 0,
    });
  }, [chatHistory, page]);

  const messageInput = useRef(null);

  // input box focus
  useEffect(() => {
    if (botOpen) {
      messageInput?.current?.focus();
    }
  }, [botOpen, chatHistory]);

  // copy to clipboard
  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  // Copy to clipboard show only for 5 seconds
  useEffect(() => {
    if (clipboard) {
      setTimeout(() => {
        setClipboard(null);
      }, 5000);
    }
  }, [clipboard]);

  return (
    <>
      {/* ChatBot */}
      <div
        className={`fixed md:flex md:flex-col md:gap-1 items-end md:justify-end z-50 ${
          botOpen
            ? "md:bottom-10 md:right-10 h-full lg:h-fit w-full md:w-fit"
            : "md:w-fit bottom-0 md:bottom-10 md:right-10 h-20 md:h-0"
        }`}
      >
        {/* If bot is closed */}
        {!botOpen && showUpMsg && (
          <>
            {chatHistory.length === 0 && (
              <div
                onClick={() => {
                  setBotOpen(true);
                  preventScroll();
                  setPage("SAGE");
                }}
                className="z-5 bg-white shadow-lg w-48 py-2 px-4 bottom-5 md:bottom-[110px] right-24 lg:right-10 absolute md:fixed rounded-md temporary-bounce animate-bounce cursor-pointer"
              >
                <p className="text-xs py-2 leading-1">
                  <strong>Hi there!👋</strong> I'm SAGE, Spinnaker Analytics'
                  Guided Explorer. How can I help you today?
                </p>
              </div>
            )}

            {chatHistory.length === 0 && (
              <span
                onClick={() => setShowUpMsg(false)}
                className="bottom-24 md:bottom-52 bg-slate-200 z-5 right-24 lg:right-10 absolute md:fixed rounded-full p-[2px] cursor-pointer temporary-bounce animate-bounce"
              >
                <IoClose className="text-slate-700 text-sm m-auto" />
              </span>
            )}
          </>
        )}
        {!botOpen && chatHistory.length < 1 && (
          <span className="rounded-full text-white bg-tertiary w-5 h-5 z-10 text-xs text-center absolute md:fixed bottom-16 md:bottom-20 right-4 md:right-10 temporary-bounce animate-bounce">
            !
          </span>
        )}
        {/* Bot toggle button */}
        <div
          className={`rounded-full cursor-pointer ${
            botOpen
              ? "absolute z-50 top-5 right-5 md:right-0 md:top-0 md:relative md:bg-gray-500 md:shadow-gray-600/50 md:opacity-75 hover:opacity-100 md:px-4 md:py-2 md:drop-shadow-lg"
              : "absolute md:fixed bottom-5 right-5 md:bottom-10 md:right-10 rounded-tr-3xl bg-gradient-to-br from-sky-700 via-sky-900 to-slate-900 shadow-primary/50 p-4 drop-shadow-lg"
          } `}
          onClick={() => {
            setBotOpen(!botOpen);
            setShowUpMsg(false);
            allowScroll();
            setPage("SAGE");
          }}
        >
          {botOpen ? (
            <>
              <IoClose className="text-white text-lg m-auto" />
            </>
          ) : (
            <div>
              <span className="relative flex">
                <span
                  className={`absolute inline-flex h-full w-full rounded-full ${
                    chatHistory.length === 0 &&
                    botOpen === false &&
                    "animate-ping bg-cyan-400 opacity-75"
                  }`}
                ></span>
                <PiChatTeardropTextFill className="text-white text-3xl" />
              </span>
            </div>
          )}
        </div>

        {/* Bot Content */}
        <div
          className={`bot drop-shadow-lg z-50 overflow-hidden h-full md:h-fit max-h-screen ${
            botOpen ? "active" : "inactive"
          }`}
        >
          {/* <ChatBot starts here */}
          <div className="bg-gray-100 text-gray-600 md:rounded-md flex text-sm flex-col justify-between overflow-hidden max-h-full h-full md:h-[60vh] lg:h-[75vh] 2xl:h-[70vh] relative w-screen md:w-96">
            <div className="h-10"></div>

            {/* Chatbot title and options */}
            <div
              className={`absolute w-full p-5 lg:py-2 xl:py-5 bg-gradient-to-br from-sky-700 via-sky-900 to-slate-900 select-none text-white rounded-b-3xl ${
                page ? "h-2/5" : "h-2/6"
              }`}
            >
              {page ? (
                <div className="flex gap-3 items-center">
                  <div
                    onClick={() => setPage(null)}
                    className="bg-white bg-opacity-50 hover:bg-opacity-100  hover:text-black p-1 h-fit flex items-center cursor-pointer justify-center rounded-sm"
                    title="Go back"
                  >
                    <FaAngleLeft className="text-lg" />
                  </div>
                  <div className="flex items-center gap-3 py-2">
                    <BsRobot className="text-3xl" />
                    <p className="text-lg">I am SAGE</p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 flex-col">
                  <img
                    src={logo}
                    alt="logo"
                    className="w-48 invert grayscale brightness-0 rounded-xl"
                  />
                </div>
              )}
            </div>

            {/* Chat div block */}
            <div
              className={`p-3 flex flex-col w-full gap-2 justify-end relative h-[85%]`}
            >
              {/* Chat Space */}
              <div className="chat h-full overflow-y-auto custom_scroll rounded-lg mb-2 relative w-full overflow-auto">
                <div
                  className={`min-h-full flex flex-col relative ${
                    page ? "justify-end bg-white shadow-inner" : ""
                  }`}
                >
                  {page ? (
                    <>
                      {/* Options for user before starting chats */}
                      {page === "SAGE" && (
                        <div className="flex flex-col my-2 p-5">
                          <div className="mb-2 w-[95%]">
                            <div className="py-2 px-2 bg-gray-100 rounded-tl-md rounded-xl rounded-br-md">
                              <p className="text-sm py-2 leading-1">
                                <strong>Hi there!👋</strong> I'm SAGE, Spinnaker
                                Analytics' Guided Explorer. How can I help you
                                today?
                              </p>
                            </div>
                            <div className="py-2 mt-2 px-2 rounded-md">
                              <div className="py-2 px-0 font-semibold">
                                Not sure? Here are a few questions to get
                                started
                              </div>
                              {/* FAQ Blocks */}
                              <div className="">
                                {FAQ_Question.map((question, index) => (
                                  <div
                                    onClick={(e) => {
                                      if (!load) {
                                        onMessageSend(e, question);
                                      }
                                    }}
                                    className={`py-2 px-2 rounded-md  text-xs 2xl:text-sm border-2 mt-2  ${
                                      load
                                        ? "border-gray-200"
                                        : "border-[#2E7E9B] hover:bg-[#E7F2F7] cursor-pointer"
                                    } `}
                                    key={index}
                                  >
                                    {question}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {chatHistory.map((history, index) => (
                            <div key={index}>
                              {/* Chat Area */}
                              <div
                                className={`flex flex-col gap-1 justify-end first:mt-0 my-3 w-full ${
                                  history.sender === "human" ? "items-end" : ""
                                }`}
                              >
                                {/* Bot or You chat title */}
                                <div className="select-none text-[11px] text-primary">
                                  {history.sender === "human" ? (
                                    <div className="flex gap-1 items-center">
                                      <FiUser className="text-lg" />
                                      <span>You</span>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 items-center">
                                      <span>SAGE </span>
                                      <BsRobot className="text-lg" />
                                    </div>
                                  )}
                                </div>
                                {/* Chats */}
                                <div className="first-letter:capitalize break-words max-w-[85%] w-fit relative">
                                  <div
                                    className={`p-3 px-4 rounded-2xl response overflow-auto ${
                                      history.sender === "human"
                                        ? "bg-gray-200 rounded-tr-sm"
                                        : "bg-gray-100 rounded-tl-sm"
                                    }`}
                                  >
                                    {/* Show streaming indicator for the message being streamed */}
                                    {streamingMessageIndex === index &&
                                    history.chat === "" ? (
                                      <div className="dot-flashing"></div>
                                    ) : (
                                      <Markdown>
                                        {truncatedMessages.has(index)
                                          ? history?.chat?.substring(0, 200) +
                                            "..."
                                          : history?.chat}
                                      </Markdown>
                                    )}

                                    {/* chat meta data - only show if not currently streaming or if there's content */}
                                    {!(
                                      streamingMessageIndex === index &&
                                      history.chat === ""
                                    ) && (
                                      <div className="flex items-start gap-1 mt-2 justify-between">
                                        {/* Read More/Less link */}
                                        {history.chat.length >= 200 &&
                                          history.sender !== "human" && (
                                            <div
                                              className="cursor-pointer hover:underline text-xs"
                                              onClick={() => {
                                                if (
                                                  truncatedMessages.has(index)
                                                ) {
                                                  // Currently truncated, show full (Read More clicked)
                                                  setTruncatedMessages(
                                                    (prev) => {
                                                      const newSet = new Set(
                                                        prev
                                                      );
                                                      newSet.delete(index);
                                                      return newSet;
                                                    }
                                                  );
                                                } else {
                                                  // Currently full, truncate it (Read Less clicked)
                                                  setTruncatedMessages((prev) =>
                                                    new Set(prev).add(index)
                                                  );
                                                }
                                              }}
                                            >
                                              {truncatedMessages.has(index)
                                                ? "Read More"
                                                : "Read Less"}
                                            </div>
                                          )}

                                        <div className="flex justify-end items-center gap-1">
                                          {/* Time stamp */}
                                          <span className="text-[11px] px-1 text-gray-500 select-none">
                                            {history.time}
                                          </span>
                                          {/* Copy to clipboard */}
                                          {history.sender !== "human" ? (
                                            <div className="cursor-pointer mb-1 text-gray-500">
                                              {clipboard === history.chat ? (
                                                <LuClipboardCheck
                                                  title="Copied to clipboard!"
                                                  className="text-gray-700"
                                                />
                                              ) : (
                                                <MdOutlineContentCopy
                                                  title="Copy to clipboard!"
                                                  onClick={() => {
                                                    copyToClipboard(
                                                      history.chat
                                                    );
                                                    setClipboard(history.chat);
                                                  }}
                                                />
                                              )}
                                            </div>
                                          ) : (
                                            // sent check mark
                                            <IoCheckmarkDoneSharp className="text-cyan-600" />
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {showFAQs &&
                                    index === chatHistory.length - 1 && (
                                      <div className="py-2 mt-2 px-2 rounded-md">
                                        <div className="py-2 px-0 font-semibold">
                                          Not sure? Here are a few questions to
                                          get started
                                        </div>
                                        <div className="">
                                          {FAQ_Question.map(
                                            (question, index) => (
                                              <div
                                                onClick={(e) => {
                                                  if (!load) {
                                                    onMessageSend(e, question);
                                                  }
                                                }}
                                                className="py-2 px-2 rounded-md cursor-pointer text-xs 2xl:text-sm border-2 mt-2 border-[#2E7E9B] hover:bg-[#E7F2F7]"
                                                key={index}
                                              >
                                                {question}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                                <div ref={scrollToBottom}></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    // Showing this block when chats appear
                    <div className="p-2 flex flex-col gap-3 w-fit overflow-hidden select-none">
                      {/* chat with the bot option */}
                      <div className="bg-white flex flex-col gap-2 rounded-md p-1 shadow-md">
                        <div className="p-2 font-semibold">Ask SAGE</div>
                        <hr />
                        <div
                          className="hover:bg-gray-100 p-3 rounded-md cursor-pointer flex gap-3 items-center"
                          onClick={() => {
                            setPage("SAGE");
                            setNewConversation(false);
                          }}
                        >
                          <BsRobot className="text-3xl" />
                          <div className="flex flex-col gap-1 relative">
                            <p className="font-semibold">SAGE</p>
                            {chatHistory.length === 0 ? (
                              <p className="text-xs">
                                Spinnaker Analytics's Guided Explorer
                              </p>
                            ) : (
                              <div className="text-xs text-ellipsis overflow-hidden">
                                {chatHistory.length > 1 &&
                                  chatHistory[
                                    chatHistory.length - 1
                                  ]?.chat.substring(0, 30)}
                                {chatHistory[chatHistory.length - 1]?.chat
                                  .length >= 30 && "..."}
                              </div>
                            )}
                            {chatHistory.length === 0 && (
                              <div className="absolute animate-bounce -top-3 -right-6 h-5 w-fit flex items-center justify-center rounded-full text-xs bg-red-500 text-white drop-shadow-lg shadow-black p-2">
                                <p className="">1</p>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* New Conversation */}
                        {chatHistory.length > 1 && (
                          <>
                            <div
                              onClick={() =>
                                setNewConversation(!newConversation)
                              }
                              className="hover:bg-gray-100 p-3 rounded-md cursor-pointer flex gap-3 items-center"
                            >
                              {newConversation ? (
                                <>
                                  <FaMinus />
                                  <span>
                                    Continue with existing conversation
                                  </span>
                                </>
                              ) : (
                                <>
                                  <FaPlus />
                                  <span>Start a new conversation</span>
                                </>
                              )}
                            </div>
                            {/* Start new conversation*/}
                            {newConversation && (
                              <div className="p-3 rounded-md cursor-pointer text-center w-full flex flex-col gap-3 items-center text-red-400">
                                This action will clear existing chat data
                                <div
                                  onClick={() => startNewConversation()}
                                  className="bg-gray-100 text-black rounded-lg p-3 w-full hover:bg-gray-200"
                                >
                                  New Conversation
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {/* Not Sure option */}
                      <div className="bg-white sticky h-full flex flex-col gap-2 rounded-md p-1 shadow-md">
                        <div className="p-2 font-semibold">
                          Not sure? Here are a few questions to get started
                        </div>
                        <hr />
                        {/* FAQ Section */}
                        <div className="h-fit md:h-[160px] overflow-y-auto chat custom_scroll">
                          {FAQ_Question.map((question, index) => (
                            <div
                              onClick={(e) => {
                                if (!load) {
                                  onMessageSend(e, question);
                                }
                              }}
                              className="hover:bg-gray-100 p-3 rounded-md cursor-pointer text-xs 2xl:text-sm"
                              key={index}
                            >
                              {question}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="absolute bottom-0 flex justify-center items-end overflow-hidden w-fit left-20 text-xs">
                        Powered by <img src={icon} alt="logo" className="w-5" />{" "}
                        <span className="font-semibold text-primary">
                          Spinnaker Analytics
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Message send, type and load*/}
              {page === "SAGE" && (
                <div className="bg-white h-fit rounded-lg w-full transition-all relative duration-500 ease-in-out">
                  {load ? (
                    <div className="p-6 pt-7">
                      <div className="dot-flashing"></div>
                    </div>
                  ) : (
                    <form action="" onSubmit={(e) => onMessageSend(e)}>
                      <div className="flex gap-5 p-5 py-3 placeholder:select-none">
                        <input
                          type="text"
                          name="message"
                          placeholder="Ask me anything.."
                          className="w-full "
                          onChange={(e) => handleOnChange(e)}
                          value={message || ""}
                          ref={messageInput}
                          autoComplete="off"
                        />
                        <div
                          className="cursor-pointer rounded-full bg-gradient-to-br from-sky-700 via-sky-900 to-slate-900 p-3"
                          onClick={(e) => onMessageSend(e)}
                        >
                          <IoSend className="text-xl text-white" />
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatBot;
